/**
 * Create Tenant → Starter content (CRM Foundation decision 9, UX artboard 13).
 *
 * Its own file so it can be render-tested: `CreateTenantPage.jsx` imports
 * `react-router-dom`, which this repo's Jest cannot resolve.
 *
 * Development, Demo and Staging start ON with a switch. Live has NO default:
 * two explicit choices, and the page stays unsubmittable until one is made.
 * When on, the licensed products are listed — only licensed ones — each
 * saying exactly what it adds. The six starter reports and the Sales Overview
 * dashboard are stated as always added, because they are, whatever is chosen.
 */
import React from 'react';
import { BarChart3, Check } from 'lucide-react';
import { describeEnvironment } from './environmentPresentation';
import {
  ALWAYS_ADDED_BODY, ALWAYS_ADDED_TITLE, SAFETY_LINES, STARTER_MODULES,
  chosenStarterModules, requiresExplicitChoice,
} from './starterContentPresentation';

const Choice = ({ selected, title, hint, onSelect, testId }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    data-testid={testId}
    className={`text-left rounded-xl border px-3 py-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      selected ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'border-input bg-card hover:border-primary/40'
    }`}
  >
    <span className="block text-sm font-medium text-foreground">{title}</span>
    <span className="block text-xs text-muted-foreground">{hint}</span>
  </button>
);

const StarterContentSection = ({
  environment, value, selectedModules, licensedModules, onChange, onModulesChange,
}) => {
  const explicit = requiresExplicitChoice(environment);
  const on = value === true;
  const chosen = chosenStarterModules(selectedModules, licensedModules);
  const envLabel = (describeEnvironment(environment)?.label || environment || '').toLowerCase();

  const toggleModule = (key) => {
    const next = chosen.includes(key) ? chosen.filter((k) => k !== key) : [...chosen, key];
    onModulesChange(next);
  };

  return (
    <div className="space-y-3" data-testid="starter-content-section">
      <div>
        <p className="text-sm font-medium text-foreground">Add sample content to this tenant?</p>
        <p className="text-xs text-muted-foreground">
          Sample records help people learn the product. They are never sent anywhere.
        </p>
      </div>

      {explicit ? (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Starter content">
            <Choice
              selected={value === true}
              title="Add sample content"
              hint="For a customer who wants to explore first"
              onSelect={() => onChange(true)}
              testId="starter-content-choice-on"
            />
            <Choice
              selected={value === false}
              title="Start empty"
              hint="For a customer bringing their own data"
              onSelect={() => onChange(false)}
              testId="starter-content-choice-off"
            />
          </div>
          {value === null || value === undefined ? (
            <p className="mt-1.5 text-xs text-destructive" data-testid="starter-content-choose">
              Choose one to continue. Live tenants have no default.
            </p>
          ) : null}
        </div>
      ) : (
        <label className="flex min-h-6 items-center justify-between gap-3 cursor-pointer" data-testid="starter-content-switch-row">
          <span>
            <span className="block text-sm font-medium text-foreground">Add sample content</span>
            <span className="block text-xs text-muted-foreground">On by default for {envLabel} tenants</span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={on}
            onChange={(e) => onChange(e.target.checked)}
            className="accent-primary"
            data-testid="starter-content-toggle"
          />
        </label>
      )}

      {on ? (
        <div className="space-y-2" data-testid="starter-content-modules">
          {STARTER_MODULES.filter((m) => licensedModules.includes(m.key)).map((m) => (
            <label
              key={m.key}
              className="flex items-start gap-3 rounded-xl border border-input bg-card p-3 cursor-pointer"
              data-testid={`starter-content-module-${m.key}`}
            >
              <input
                type="checkbox"
                checked={chosen.includes(m.key)}
                onChange={() => toggleModule(m.key)}
                className="mt-0.5 accent-primary"
                data-testid={`starter-content-module-toggle-${m.key}`}
              />
              <span>
                <span className="block text-sm font-medium text-foreground">{m.label}</span>
                <span className="block text-xs text-muted-foreground">{m.adds}</span>
              </span>
            </label>
          ))}
          <p className="text-xs text-muted-foreground">Only licensed products appear.</p>
        </div>
      ) : null}

      <div
        className="flex gap-2.5 rounded-xl border border-success/30 bg-success/10 px-3 py-2.5 text-xs text-foreground"
        data-testid="starter-content-always"
      >
        <BarChart3 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          <span className="block font-semibold">{ALWAYS_ADDED_TITLE}</span>
          {ALWAYS_ADDED_BODY}
        </span>
      </div>

      {on ? (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="starter-content-safety">
          {SAFETY_LINES.map((line) => (
            <li key={line} className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />{line}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default StarterContentSection;
